import React, {Fragment} from 'react';
import {Flex, Button, Link, Breadcrumbs, Separator, Layout, Dropdown, Form, Tags, DurationPicker, Submit, Edit, Textarea, List, Suggestions, Goals} from 'components';
import {SubRoutines, SubTasks, Repetition, GoalsDropdown, Time, PriorityPopup, Image, GoogleCalendar} from '.';
import {over, capitalize, get, fullday, assign, flowRight, capitalizeWords, useSearchParams, useNamedSelector, partialRight, omit, useSelector} from 'helpers';
import {store, activities, units} from 'store';
import moment from 'moment/moment.js';

export function EditForm({ modal, id, goal, milestone, date, isBlockType, dailyRoutineType, module, occurance, contextDate, defaultType = module }) {
  const { today, tomorrow } = useNamedSelector('calendar.days');
  const { activity, image, subroutines = [], subtasks = [], perDay } = useNamedSelector('activities.details', { id, context: 'activities' });
  const unit = useSelector(units.getById)(activity?.unitId);

  const initialValues = {
    ...useSearchParams(),
    image,
    subroutines,
    subtasks,
    dailyRoutineType: isBlockType ? 'time_block' : dailyRoutineType,
    goal_id: goal?.id || milestone?.id,
    repetition: {},
    ...activity,
    unit: unit?.name,
    name: isBlockType ? 'Blocked' : activity?.name,
  };

  const reschedulable = id && activity.repeatType && contextDate && perDay < 2;

  // populate repetition
  if (activity) {
    assign(initialValues.repetition, activities.repetitionValues(activity));
    // if (reschedulable && (occurance?.priority || occurance?.duration)) {
    //   initialValues.time = occurance.priority;
    //   initialValues.duration = occurance.duration;
    // }
  } else if (/^(habit|routine)/.test(defaultType)) {
    initialValues.repetition = {
      isEmpty: true,
      defaultType: /habit/.test(defaultType) ? 'habit' : 'routine',
    };
  } else if (module == 'morning-routine') {
    initialValues.repetition.ts = today.unix();
  } else if (module == 'evening-routine') {
    initialValues.repetition.ts = tomorrow.unix();
  } else if (date) {
    initialValues.repetition.ts = date.unix();
  }

  if (initialValues.allDay) {
    initialValues.duration = fullday;
  }

  const submit = ({subtasks, subroutines, image, repetition, ...activity}) => {
    // const differs = activity.time !== initialValues.time || activity.duration !== initialValues.duration;
    // if (differs && reschedulable && activity.repeatType) {
    //   activities.reschedule({
    //     id: activity.id,
    //     date: contextDate,
    //     time: activity.time,
    //     duration: activity.duration,
    //     occurance,
    //     contextDate
    //   });
    //   activity = omit(activity, 'time', 'duration');
    // }

    const unitId = units.dispatch.upsert(activity.unit).unit?.serverId ?? 0;

    activities.update({ ...activity, ...repetition, unitId }, {
      subroutines: subroutines.filter(hasName),
      subtasks: subtasks.filter(hasName),
      file: {
        ...image,
        remove: image === null,
        modelType: 'activity',
        modelId: activity.id || 'activityId',
      },
      historyReplace: module && !activity.id && `../{activityId}/`,
      historyPush: module && `/${module}/`
    });
  };

  return (
    <Form initialValues={initialValues} onSubmit={over(submit, modal?.close)} delay>
      { ({ values }) =>
        <Fragment>
          <Layout.Header justified>
            <Breadcrumbs>
              { capitalize(module) }
              { date && values.repetition.ts && moment.unix(values.repetition.ts).calendar() }
              { goal && <Link to="/goals/">Goals</Link> }
              { goal && <Link to={`/goals/${goal.id}/`}>{goal.name}</Link> }
              { milestone && 'Milestones'}
              { milestone && <Link to={`/goals/${milestone.id}/`}>{milestone.name}</Link> }
              <b>{ activity ? 'Edit ' : 'Add New'} {getType(values)}</b>
            </Breadcrumbs>
            { !modal &&
              <Submit className="small">
                {[ 'Save', 'Saving..', activity && 'Saved' ]}
              </Submit>
            }
          </Layout.Header>
          <List small-gutter>
            <Edit
              className="bold"
              name="name"
              placeholder={`Enter ${ values.dailyRoutineType == 'daily_routine' ? 'name of ' : '' }${getType(values)}..`}
              required
              autoFocus={Boolean(modal)}
              Suggestions={!modal && getSuggestions(values)}
            />

            <Textarea single name="note" placeholder="Enter Note.." />

            <Repetition allow={[
              values.dailyRoutineType != 'daily_routine' && 'dayFrequency',
              values.dailyRoutineType != 'daily_routine' && (values.id || !/^(habit|routine)/.test(defaultType)) && 'onetime',
              'recurring',
              !values.googleCalendarId && 'someday',
            ]} />

            { !activities.allDay(values) &&
              <Time label={values.dailyRoutineType ? 'Start Time' : 'Time'} />
            }

            { values.dailyRoutineType != 'daily_routine' &&
              <DurationPicker name="duration" label="Duration" hasSuggestions />
            }

            { values.dailyRoutineType == 'daily_routine' &&
              <Fragment>
                <Separator heading="Subroutines" />
                <SubRoutines name="subroutines" />
              </Fragment>
            }

            { values.dailyRoutineType != 'daily_routine' &&
              <Fragment>
                <Separator heading="Subtasks" />
                <SubTasks name="subtasks" />
              </Fragment>
            }

            <Separator heading="Details" />

            { values.repetition.repeatType && values.dailyRoutineType != 'daily_routine' &&
              <Edit type="number" name="numericalTarget" label="Numerical Target" Suffix={Goals.NumberUnit} unitName="unit" />
            }

            <Tags name="tagIds" />

            <Image name="image" />

            <Dropdown name="priority" label="Priority" Popup={PriorityPopup} />

            { !goal && !milestone &&
              <GoalsDropdown name="goal_id" />
            }

            <GoogleCalendar name="googleCalendarId" />
          </List>
          { modal &&
            <Layout.Footer>
              <Flex className={['right', 'spacing', 'small']}>
                <Button onClick={modal.close} className="borderless">Cancel</Button>
                <Submit>{[ 'Save', 'Saving..' ]}</Submit>
              </Flex>
            </Layout.Footer>
          }
        </Fragment>
      }
    </Form>
  );
}

function hasName({ name = '' }) {
  return Boolean(name.trim());
}

const getType = flowRight(capitalizeWords, ({ repetition, ...values }) => {
  if (values.dailyRoutineType == 'daily_routine') {
    return 'daily ritual';
  }
  return repetition.defaultType || activities.type({
    ...values,
    ...repetition
  });
});

function getSuggestions({ habitTypeId, repetition }) {
  if (habitTypeId) {
    return partialRight(Suggestions.Routines, { habitTypeId });
  } else if (repetition.repeatType || repetition.defaultType == 'routine') {
    return Suggestions.Routines;
  }
}
